import React, { useEffect, useState } from "react";
import { Landmark, Check, X } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Badge, Loading, fmtMoney, EmptyState } from "../components/ui";

const FIELDS = [
  ["sec80c", "80C — PF / ELSS / LIC (max ₹1.5L)"],
  ["sec80d", "80D — Health Insurance (max ₹75k)"],
  ["hraExemption", "HRA Exemption (annual rent-based)"],
  ["homeLoanInterest", "Home Loan Interest (max ₹2L)"],
  ["nps", "80CCD(1B) — NPS (max ₹50k)"],
  ["other", "Other deductions"],
];

function MyDeclaration() {
  const [d, setD] = useState(null);
  const load = () => api.get("/payroll/tax-declaration").then(({ data }) => setD(data));
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/payroll/tax-declaration", d); alert("Declaration submitted for HR approval"); load(); }
    catch (err) { alert(apiError(err.response?.data?.detail)); }
  };
  if (!d) return <Loading />;
  const total = d.regime === "OLD" ? FIELDS.reduce((s, [k]) => s + (+d.investments?.[k] || 0), 0) : 0;
  return (
    <form onSubmit={save} className="space-y-5">
      <Card className="p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div><h3 className="font-head text-base font-bold text-slate-900 dark:text-white">FY {d.fy} Declaration</h3>
            <p className="text-sm text-slate-500">Pick your tax regime — TDS in payroll is computed accordingly.</p></div>
          <Badge value={d.status} tone={d.status === "APPROVED" ? "ok" : d.status === "REJECTED" ? "bad" : "warn"} />
        </div>
        <div className="mb-5 flex gap-2">
          {["NEW", "OLD"].map((r) => (
            <button type="button" key={r} data-testid={`regime-${r}`} onClick={() => setD({ ...d, regime: r })}
              className={`rounded-lg border px-4 py-2 text-sm font-semibold ${d.regime === r ? "border-accent bg-accent text-white" : "border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300"}`}>
              {r === "NEW" ? "New Regime (default)" : "Old Regime (with deductions)"}
            </button>
          ))}
        </div>
        {d.regime === "OLD" ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map(([k, label]) => (
              <div key={k}><label className="label">{label}</label>
                <input type="number" min="0" data-testid={`tax-${k}`} value={d.investments?.[k] || 0}
                  onChange={(e) => setD({ ...d, investments: { ...d.investments, [k]: +e.target.value } })} className="input" /></div>
            ))}
          </div>
        ) : (
          <p className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500 dark:bg-slate-800/50">The new regime applies a ₹75,000 standard deduction with nil tax up to ₹12L taxable income. Investment declarations are not required.</p>
        )}
        <div className="mt-5 flex items-center justify-between">
          {d.regime === "OLD" && <p className="text-sm text-slate-500">Total declared: <b className="text-slate-800 dark:text-slate-100">{fmtMoney(total)}</b></p>}
          <button data-testid="tax-submit" className="btn-accent ml-auto">Submit Declaration</button>
        </div>
      </Card>
    </form>
  );
}

function HRDeclarations() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/payroll/tax-declarations").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const decide = async (id, status) => { try { await api.put(`/payroll/tax-declarations/${id}`, { status }); load(); } catch (e) { alert(apiError(e.response?.data?.detail)); } };
  if (!items) return <Loading />;
  return items.length === 0 ? <EmptyState icon={Landmark} title="No declarations submitted yet" /> : (
    <Card className="overflow-hidden"><div className="overflow-x-auto">
      <table className="w-full" data-testid="tax-declarations-table">
        <thead><tr><th className="th">Employee</th><th className="th">Regime</th><th className="th">Declared Deductions</th><th className="th">Status</th><th className="th">Action</th></tr></thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {items.map((t) => (
            <tr key={t.id} data-testid={`tax-decl-${t.id}`}>
              <td className="td font-medium text-slate-800 dark:text-slate-100">{t.employeeName} <span className="text-xs text-slate-400">{t.employeeCode}</span></td>
              <td className="td"><Badge value={t.regime} tone={t.regime === "OLD" ? "info" : "neutral"} /></td>
              <td className="td font-mono">{fmtMoney(t.totalDeductions || 0)}</td>
              <td className="td"><Badge value={t.status} tone={t.status === "APPROVED" ? "ok" : t.status === "REJECTED" ? "bad" : "warn"} /></td>
              <td className="td"><div className="flex gap-1.5">
                {t.status === "SUBMITTED" && <>
                  <button data-testid={`tax-approve-${t.id}`} onClick={() => decide(t.id, "APPROVED")} className="rounded-md bg-emerald-100 p-1.5 text-emerald-600 dark:bg-emerald-500/15"><Check size={14} /></button>
                  <button data-testid={`tax-reject-${t.id}`} onClick={() => decide(t.id, "REJECTED")} className="rounded-md bg-red-100 p-1.5 text-red-600 dark:bg-red-500/15"><X size={14} /></button>
                </>}
              </div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div></Card>
  );
}

export default function TaxCenter() {
  const { user } = useAuth();
  const [tab, setTab] = useState("me");
  const isHR = ["SUPERADMIN", "HR_ADMIN"].includes(user?.role);
  return (
    <div>
      <PageHeader title="Tax Center" subtitle="Regime selection & investment declarations feeding payroll TDS" />
      {isHR && (
        <div className="mb-6 flex gap-1 border-b border-slate-200 dark:border-slate-800">
          {[["me", "My Declaration"], ["all", "All Declarations"]].map(([k, l]) => (
            <button key={k} data-testid={`tax-tab-${k}`} onClick={() => setTab(k)} className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium ${tab === k ? "border-accent text-accent" : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}>{l}</button>
          ))}
        </div>
      )}
      {tab === "me" ? <MyDeclaration /> : <HRDeclarations />}
    </div>
  );
}
